import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import { MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { DEFAULT_LOCALE } from './constants';
import arbManifest from 'translations/arbManifest';
import { resolveBundleUrlWithFallbacks } from './helpers';
import { MyTopNavigation } from './components/common/navigation';
import { Sidebar, DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED, NAV_HEIGHT } from './components/common/Sidebar';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import AppRouter from './components/AppRouter';
import { ChatWidget } from './components/agent';
import { createAppTheme } from './theme';
import { createEmotionCache } from './utils/createEmotionCache';
import { createQueryClient } from './config/queryClient';
import { applyTheme, ACTIVE_THEME } from './theme/colorThemes';
import { startPrefetching } from './services/prefetch';
import { useDarkMode } from './hooks/useDarkMode';

// Apply the active color theme on app initialization
applyTheme(ACTIVE_THEME);

// Start background prefetching of data
startPrefetching();

const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
        .withLocale(DEFAULT_LOCALE)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
    resolveBundleUrl: (
        resourcePackPath: string | undefined,
        locale: string,
        bundle: string
    ) => resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
};

// Create QueryClient instance for React Query
const queryClient = createQueryClient();

// Create Emotion cache for CSP compliance in production
const cache = createEmotionCache();

/**
 * Main App component for AI Document Tabular Review
 * 
 * Architecture:
 * - ErrorBoundary: Catches and handles React errors gracefully
 * - CacheProvider: Configures Emotion cache for CSP compliance (production)
 * - QueryClientProvider: Manages server state and caching
 * - MbmProvider: Handles localization
 * - ThemeProvider: Provides consistent Material-UI styling
 * - MyTopNavigation: Fixed top navigation bar
 * - Sidebar: Left navigation drawer
 * - AppRouter: React Router based routing system
 * - ReactQueryDevtools: Development debugging tools (conditionally loaded)
 */
const App = () => {
    // Dark mode state - persisted in localStorage
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const appTheme = useMemo(() => createAppTheme(isDarkMode ? 'dark' : 'light'), [isDarkMode]);

    // Sidebar collapsed state - persisted in localStorage
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebar-collapsed');
        return saved === 'true';
    });

    // Persist sidebar state
    useEffect(() => {
        localStorage.setItem('sidebar-collapsed', sidebarCollapsed.toString());
    }, [sidebarCollapsed]);

    const handleToggleSidebar = () => {
        setSidebarCollapsed(prev => !prev);
    };

    const currentDrawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

    const content = (
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <MbmProvider {...mbmOptions}>
                    <ThemeProvider theme={appTheme}>
                        <CssBaseline />
                        <BrowserRouter>
                            <Box sx={{ display: 'flex' }}>
                                <MyTopNavigation isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} />
                                <Sidebar 
                                    collapsed={sidebarCollapsed}
                                    onToggleCollapse={handleToggleSidebar}
                                />
                                <Box
                                    component="main"
                                    sx={{
                                        flexGrow: 1,
                                        width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                                        ml: { sm: `${currentDrawerWidth}px` },
                                        mt: `${NAV_HEIGHT}px`,
                                        py: 2,
                                        px: 2,
                                        transition: 'margin-left 0.2s ease-in-out, width 0.2s ease-in-out',
                                    }}
                                >
                                    <ErrorBoundary>
                                        <AppRouter />
                                    </ErrorBoundary>
                                </Box>
                                <ErrorBoundary>
                                    <ChatWidget />
                                </ErrorBoundary>
                            </Box>
                        </BrowserRouter>
                    </ThemeProvider>
                </MbmProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    );

    // Only wrap with CacheProvider in production for CSP compliance
    if (process.env.NODE_ENV === 'production') {
        return <CacheProvider value={cache}>{content}</CacheProvider>;
    }

    return content;
};

export default App;
